import { useEffect, useState } from 'react';
import { LoaderCircle } from 'lucide-react';

import { ApiError } from '@/api/client';
import { useAdminPaymentSettings, useUpdatePaymentSettings } from '@/api/hooks/useSuperadmin';
import type { PaymentInfo } from '@/api/payments';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from '@/components/ui/use-toast';

function toFormState(info: PaymentInfo) {
  return {
    upiId: info.upiId,
    upiName: info.upiName,
    bankName: info.bankName,
    bankAccountNo: info.bankAccountNo,
    bankIfsc: info.bankIfsc,
    bankAccountName: info.bankAccountName,
    bankBranch: info.bankBranch,
    qrImageUrl: info.qrImageUrl,
    whatsappNumber: info.whatsappNumber,
    supportEmail: info.supportEmail,
    instructions: info.instructions,
  };
}

export function PaymentSettingsTab() {
  const { data, isLoading } = useAdminPaymentSettings();
  const updateSettings = useUpdatePaymentSettings();

  const [form, setForm] = useState<ReturnType<typeof toFormState> | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (data) setForm(toFormState(data));
  }, [data]);

  function set<K extends keyof NonNullable<typeof form>>(key: K, value: NonNullable<typeof form>[K]) {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }

  async function handleSave() {
    if (!form) return;
    setError(null);
    try {
      await updateSettings.mutateAsync({
        upiId: form.upiId.trim(),
        upiName: form.upiName.trim(),
        bankName: form.bankName.trim(),
        bankAccountNo: form.bankAccountNo.trim(),
        bankIfsc: form.bankIfsc.trim(),
        bankAccountName: form.bankAccountName.trim(),
        bankBranch: form.bankBranch.trim(),
        qrImageUrl: form.qrImageUrl.trim(),
        whatsappNumber: form.whatsappNumber.trim(),
        supportEmail: form.supportEmail.trim(),
        paymentInstructions: form.instructions.trim(),
      });
      toast({ title: 'Payment settings saved', variant: 'success' });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  if (isLoading || !form) {
    return (
      <Card>
        <CardContent className="space-y-3 py-6">
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
          <Skeleton className="h-10" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <p className="text-sm text-muted-foreground">
          Shown to every tenant on their Subscription page when they go to pay.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ps-upiid">UPI ID</Label>
            <Input id="ps-upiid" value={form.upiId} onChange={(e) => set('upiId', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ps-upiname">UPI payee name</Label>
            <Input id="ps-upiname" value={form.upiName} onChange={(e) => set('upiName', e.target.value)} />
          </div>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ps-bankname">Bank name</Label>
            <Input id="ps-bankname" value={form.bankName} onChange={(e) => set('bankName', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ps-bankbranch">Branch</Label>
            <Input id="ps-bankbranch" value={form.bankBranch} onChange={(e) => set('bankBranch', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ps-accno">Account number</Label>
            <Input id="ps-accno" value={form.bankAccountNo} onChange={(e) => set('bankAccountNo', e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ps-ifsc">IFSC</Label>
            <Input id="ps-ifsc" value={form.bankIfsc} onChange={(e) => set('bankIfsc', e.target.value.toUpperCase())} maxLength={20} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="ps-accname">Account holder name</Label>
            <Input id="ps-accname" value={form.bankAccountName} onChange={(e) => set('bankAccountName', e.target.value)} />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ps-qr">QR image URL</Label>
          <Input id="ps-qr" value={form.qrImageUrl} onChange={(e) => set('qrImageUrl', e.target.value)} placeholder="https://…" />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ps-whatsapp">WhatsApp number</Label>
            <Input id="ps-whatsapp" value={form.whatsappNumber} onChange={(e) => set('whatsappNumber', e.target.value)} placeholder="+91…" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ps-email">Support email</Label>
            <Input id="ps-email" type="email" value={form.supportEmail} onChange={(e) => set('supportEmail', e.target.value)} />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ps-instructions">Payment instructions</Label>
          <Input id="ps-instructions" value={form.instructions} onChange={(e) => set('instructions', e.target.value)} maxLength={1000} />
        </div>

        {error && (
          <p role="alert" className="rounded-md border border-status-expired/30 bg-status-expired/10 px-3 py-2 text-sm text-status-expired">
            {error}
          </p>
        )}

        <Button onClick={handleSave} disabled={updateSettings.isPending}>
          {updateSettings.isPending && <LoaderCircle className="animate-spin" />}
          Save payment settings
        </Button>
      </CardContent>
    </Card>
  );
}
